import { locationSearchMapConfig } from "./config";
import { moveMapTo } from "./moveMapTo";
import { ResultsList } from "./ResultsList";
import { locationSearchMapConfig as config } from './config';
import { LatLng } from "./models/LatLng";
import { startLoader } from "../util/loader";
import { reportScreenSize } from "./reportScreenSize";
import { ScreenType } from "./models/ScreenType";
import { isLocalized } from "../util/localizationService";
import { getBrandDetailsFromBrandJson } from "../util/share";

export class Search
{
    map: H.Map;
    resultsList: ResultsList;
    private searchResMapIcon: H.map.Icon | undefined;
    marker!: H.map.Marker;
    pinFirstResult: boolean = false;
    newMapComponent: boolean = false;
    searchText: string = '';
    shouldResetList: boolean = true;
    locationReorderedForCA: boolean = false;
    isInitialLoad: boolean = true;
    constructor(map: H.Map, resultsList: ResultsList)
    {
        this.map = map;
        this.resultsList = resultsList;
        this.initializeProperties();
        this.setupEventListeners();
        this.initializeSearchTerm();
    }

    private initializeProperties(): void {
        this.searchResMapIcon = new H.map.Icon(searchResMarker);
        this.locationReorderedForCA = sessionStorage.getItem('locationReorderedForCA') === 'true';
        this.shouldResetList = true;
    }

    private setupEventListeners(): void {
        this.setupMapEventListeners();
        this.setupSearchInputListeners();
        this.setupButtonListeners();
        this.setupMobileViewToggles();
        this.setupModalCloseHandlers();
    }

    private setupMapEventListeners(): void {
        this.map.addEventListener('mapviewchangeend', () => {
            if (this.isInitialLoad) {
                this.isInitialLoad = false;
                return;
            }
            this.newMapComponent = true;
            const viewModel = this.map?.getViewModel();
            const lookAtData = viewModel?.getLookAtData();
            const bounds = lookAtData?.bounds;

            if (bounds && this.shouldResetList) {
                const shouldReorderForCA = !this.locationReorderedForCA && this.searchText === 'canada';
                this.resultsList.filterResults(bounds.getBoundingBox(), shouldReorderForCA);
            }
            const objects = this.map.getObjects();
            if (objects.some(obj => obj instanceof H.map.Marker)) {
                const markerGeometry = this.marker.getGeometry() as H.geo.Point;
                this.resultsList.sortCurrentListByDistanceToPin(markerGeometry);
            }

            if (this.pinFirstResult) {
                this.resultsList.pinFirstResult();
            } else {
                this.resultsList.unpinFirstResult();
            }
        });
    }

    private setupSearchInputListeners(): void {
        const locationSearchInput = document.getElementById('location-search') as HTMLInputElement;

        locationSearchInput?.addEventListener('keydown', async (e: KeyboardEvent) => {
            if (e.code === 'Enter') {
                e.preventDefault();
                const searchText = (e.target as HTMLInputElement).value.trim()?.toLowerCase();
                if (searchText) {
                    await this.search(searchText);
                }
                else {
                    await this.search(searchText, true);
                }
            }
        });

        locationSearchInput?.addEventListener('input', () => {
            const value = locationSearchInput?.value ?? "";
            this.searchText = value.trim().toLowerCase();
            if (!this.locationReorderedForCA && this.searchText === 'canada') {
                this.resultsList.reorderLocationsForCA();
            }
        });
    }

    private setupButtonListeners(): void {
        const findMrElectButton = document.querySelector('.location-search-form .form-cta .primary-btn') as HTMLButtonElement;
        const useMyLocationBtn = document.querySelector('.location-search-wrapper .use-my-location-btn') as HTMLButtonElement;

        findMrElectButton?.addEventListener('click', () => {
            const searchText = (document.getElementById('location-search') as HTMLInputElement).value;
            this.search(searchText);
        });

        useMyLocationBtn?.addEventListener('click', () => {
            this.knowMyLocationSearch();
        });
    }

    private setupMobileViewToggles(): void {
        const mobileWrapper = document.querySelector('.map-wrapper-mobile') as HTMLElement;
        if (!mobileWrapper) return;

        mobileWrapper.style.display = 'none';

        const showHideDiv = (val: number) => {
            mobileWrapper.style.display = val === 1 ? 'block' : 'none';
        };

        const button1 = document.getElementById('map-view') as HTMLButtonElement | null;
        const button2 = document.getElementById('list-view') as HTMLButtonElement | null;

        button1?.addEventListener('click', () => showHideDiv(1));
        button2?.addEventListener('click', () => showHideDiv(2));
    }

    private setupModalCloseHandlers(): void {
        const mapModal = document.getElementById("map-modal") as HTMLElement;
        const mapModalMobile = document.getElementById("map-modal-mobile") as HTMLElement;
        if(mapModal) {
            const mapModalClose = document.getElementById("close-map-modal");
            mapModalClose?.addEventListener('click', () => {
                mapModal.style.display = "none";
            });
        }

        if (mapModalMobile) {
            const mapModalCloseMobile = document.getElementById("close-map-modal-mobile");
            mapModalCloseMobile?.addEventListener('click', () => {
                mapModalMobile.style.display = "none";
            });
        }
    }

    private initializeSearchTerm(): void {
        const locationSearchInput = document.getElementById('location-search') as HTMLInputElement;
        if (!locationSearchInput) return;
        if (isLocalized()) {
            const localizedZip = this._getLocalizedZip();
            if (localizedZip) {
                this._initiateSearchFromZip(locationSearchInput, localizedZip);
            }
        } else {
            locationSearchInput.value = sessionStorage.getItem('temp_zipcode') ?? '';
            sessionStorage.removeItem("temp_zipcode");
            this.knowMyLocationSearch();
        }
    }

    private _getLocalizedZip(): string | null {
        let localizedZip = localStorage.getItem('zipcode');
        if (localizedZip) {
            return localizedZip;
        }

        try {
            const cachedData = localStorage.getItem('fullAttrOnLoad');
            if (cachedData) {
                const fullAttributeData = JSON.parse(cachedData);
                return fullAttributeData?.zip ?? null;
            }
        } catch (error) {
            console.error('Error parsing fullAttrOnLoad from localStorage:', error);
        }

        return null;
    }

    private _initiateSearchFromZip(inputElement: HTMLInputElement, zip: string): void {
        inputElement.value = zip;
        const trimmedZip = zip.trim();
        if (trimmedZip) {
            this.search(trimmedZip, false);
        }
        else {
            this.search(trimmedZip, true);
        }
    }

    async search(searchQuery: string, isRemove: boolean = true)
    {
        this.handleSessionStorageCleanup(isRemove);
        this.shouldResetList = true;
        
        const normalizedQuery = this.normalizeSearchQuery(searchQuery);
        this.setPinFirstResult(normalizedQuery);
        
        const resDBASearch = this.resultsList.searchByDBAName(normalizedQuery);
        localStorage.setItem("resDBASearchLength",resDBASearch.length);
        
        if (this.isDBASearch(normalizedQuery)) {
            this.loadSearchByDBAResult(resDBASearch);
            return;
        }
        
        if (normalizedQuery.length === 0) {
            this.handleEmptySearch();
            return;
        }
        
        await this.performHereMapSearch(normalizedQuery, resDBASearch);
    }

    /** windsurf generated code start */
    private handleSessionStorageCleanup(isRemove: boolean): void {
        if (isRemove) {
            sessionStorage.removeItem('nearbyLocations');
        }
    }
    
    private normalizeSearchQuery(searchQuery: string): string {
        if (searchQuery) {
            return searchQuery.trim();
        }
        
        const locationSearchInput = document.getElementById('location-search') as HTMLInputElement | null;
        return locationSearchInput?.value?.trim() ?? "";
    }
    
    private handleEmptySearch(): void {
        console.log('reset search and map');
        this.resultsList.unpinFirstResult();
        if (this.shouldResetList) {
            this.resetMap();
            this.resultsList.resetList();
        }
    }
    
    private async performHereMapSearch(searchQuery: string, resDBASearch: any): Promise<void> {
        const searchBox = document.getElementById('location-search') as HTMLInputElement;
        if (searchBox?.value === '') {
            return;
        }
        
        try {
            const res = await this.heremapSearch(searchQuery);
            this.handleHereMapResults(res, resDBASearch);
        } catch (error) {
            console.error('Here map search failed:', error);
            this.loadSearchByDBAResult(resDBASearch);
        }
    }
    
    private handleHereMapResults(res: any, resDBASearch: any): void {
        if (res.items?.length > 0) {
            const firstItem = res.items[0];
            const type = this.determineResultType(firstItem);
            this.focusMap(firstItem.position, type);
            this.addMarker(firstItem.position);
            this.resultsList.unpinFirstResult();
        } else {
            this.loadSearchByDBAResult(resDBASearch);
        }
    }
    
    private determineResultType(item: any): string {
        const resultType = item.resultType;
        if (['street', 'place', 'postalCodePoint'].includes(resultType)) {
            return resultType;
        }
        return item[`${resultType}Type`];
    }
    /** windsurf generated code end */

    async heremapSearch(searchQuery: string)
    {
        let apiUrl = `${locationSearchMapConfig.geoCodeSearchApiUrl}?apiKey=${locationSearchMapConfig.apiKey}&q=${searchQuery}&in=countryCode:USA,CAN,PRI`;
     
        const res = await fetch(apiUrl)
            .then(data => data.json());
        return res;
    }
    resetMap()
    {
        const screenType = reportScreenSize();
        moveMapTo(this.map,
            { lat: Number(config.initView.lat), lng: Number(config.initView.lng) },
            Number(screenType === ScreenType.Desktop ? config.initView.zoom : config.initView.mobileZoom));
    }
    focusMap(position: LatLng, areaType: string)
    {
        //We are setting the zoom level in a hidden field - fetchcity-zoomlevel. If the value is not set or ivalid will fetch from getZoom method
        const zoomInput = document.getElementById("fetchcity-zoomlevel") as HTMLInputElement | null;
        const inputZoomValue = zoomInput?.value.trim();
        const inputZoom = inputZoomValue ? Number.parseFloat(inputZoomValue) : Number.NaN;
        const zoom = !Number.isNaN(inputZoom) ? inputZoom : this.getZoom(areaType);
        moveMapTo(this.map, position, zoom);
    }
    async knowMyLocationSearch() {
        const conceptCode = document.getElementById('conceptCode') as HTMLInputElement;
        const brandData = await getBrandDetailsFromBrandJson(conceptCode?.value);

        const showLocationsFor = brandData?.default_country_locations?.toUpperCase();

        navigator.geolocation.getCurrentPosition(
            (position) => {
            const coords = `${position.coords.latitude},${position.coords.longitude}`;
            const revGeocodeUrl = `${config.revGeoCodeSearchApiUrl}?at=${coords}&apiKey=${config.apiKey}`;
            fetch(revGeocodeUrl)
                .then(data => data.json())
                .then(res => {
                    if (res.items.length > 0) {
                        const zipcode = res.items[0].address.postalCode;
                        const searchBox = document.getElementById('location-search') as HTMLInputElement;
                            if (searchBox) {
                                searchBox.value = zipcode;
                            }
                           
                            this.search(zipcode);
                        } 
                    })
                    .catch(error => {
                        console.error('Error fetching reverse geocode:', error);
                        
                    });
            },
            (error) => {
                if (error.code === error.PERMISSION_DENIED && !isLocalized()) {
                    try {
                        this.shouldResetList = false;
                        this.resultsList.filterByCountry(showLocationsFor);
                    } catch (e) {
                        console.error('Error calling filterByCountry:', e);
                    }
                }
            }
        );
    }
    
    loadSearchByDBAResult(resDBASearch: any)
    {
        if (resDBASearch.length > 0)
        {
            //console.log('loadSearchByDBAResult: focusing by dba name: ');
            const topResDBASearch = resDBASearch[0].item;
            const bbox = this.getBoundingBox(topResDBASearch.latitude, topResDBASearch.longitude, 0.125);
            this.resultsList.filterResults(bbox);
            const position = { lat: topResDBASearch.latitude, lng: topResDBASearch.longitude };
            this.focusMap(position, 'place');
            this.addMarker(position);
        }else {
            const locationFoundText = document.querySelector('.location-total-text') as HTMLElement;  ;
            const noLosAvailableMsg = document.getElementById('no-locs-available-msg') as HTMLElement;  
            locationFoundText.innerHTML = noLosAvailableMsg?.innerHTML;
            locationFoundText.style.lineHeight = "normal";
            locationFoundText.style.display = "block";
            let locationList = document.querySelector(".location-list") as HTMLUListElement;
            if(locationList){
                locationList.style.display = "none";
            }
            return;
        }
    }
    
    getBoundingBox = (lat: number, lng: number, range: number): H.geo.Rect =>
    {
        const north = lat + range;
        const south = lat - range;
        const east = lng + range;
        const west = lng - range;
        const bbox = new H.geo.Rect(north, west, south, east);
        return bbox;
    }
    getZoom(areaType: string){
        let zoom = 4;
        if (areaType === 'street')
        {
            zoom = 16;
        }
        else if (areaType === 'place')
        {
            zoom = 14;
        }
        else if (areaType === 'postalCode' || areaType === 'postalCodePoint')
        {
            zoom = 9; // 20km zoom
        }
        else if (areaType === 'city')
        {
            zoom = 9; // 20km zoom
        }
        else if (areaType === 'state')
        {
            zoom = 6;
        }
        else if (areaType === 'country')
        {
            zoom = 5;
        }
        return zoom;
    }
    addMarker(position: LatLng){        
        if(this.marker) {
            this.map.removeObject(this.marker);
        }
        this.marker = new H.map.Marker(position, 
            {icon: this.searchResMapIcon});
        this.map.addObject(this.marker);
    }
    setPinFirstResult(query: string){
        this.pinFirstResult = this.isDBASearch(query) || this.isZipSearch(query);
    }
    isDBASearch(query: string){
        const dbaNameElement = document.getElementById("dbaName") as HTMLInputElement;
        const fullDbaNameElement = document.getElementById("full_dbaName") as HTMLInputElement;
        const val = (dbaNameElement?.value || fullDbaNameElement?.value)?.replace(/-/g,"")?.toLowerCase();
        if(!val || val?.length === 0) {
            return false;
        }
        return query.replace(/\W/g, "").toLowerCase().includes(val);
    }
    isZipSearch(query: string){
        return /\d{5}/.test(query.trim());
    }
}
const searchResMarker = '<svg xmlns="http://www.w3.org/2000/svg" id="a" viewBox="0 0 59 59" width="44" height="42"><defs><style>.b{fill:#fff;}</style></defs><path d="M47.86,23.09c0-11.23-9.11-20.34-20.34-20.34S7.18,11.86,7.18,23.09c0,6.89,3.44,12.98,8.69,16.66l11.66,11.66,11.66-11.66c5.25-3.68,8.69-9.76,8.69-16.66Z"/><circle class="b" cx="27.52" cy="23.09" r="12.27"/></svg>';